import { useContext } from 'react'
import { CheckCircleFilled } from '@ant-design/icons'
import { Col, Row, Tag } from 'antd'
import { ImOrderDetailsDisplay } from '@Orders/OrderDetailsPage/Status/ImOrderDetailsDisplay'
import { OrderContext } from '@Orders/OrderDetailsPage/context'
import {
    MOLD_DESIGN_IN_PROGRESS,
    PART_PRODUCTION_COMPLETE,
} from '@Orders/constants'
import styles from '@Orders/OrderDetailsPage/styles.css'

import Box from '~/core/Box'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import { ImOrderItemType } from '../types'
import { moldOrderSteps, partRelatedSteps } from '../constants'
import { isPartsProductionStatusComplete } from '../logic'
import { PROCESS_DISPLAY_IM_SAMPLE } from '~/lib/partsConstants'

import Steps from './Steps'

const getIcon = (steps: string[], step: string, selectedStep: string) => {
    const stepIndex = steps.indexOf(step)

    const selectedStepIndex =
        steps.indexOf(selectedStep) === -1
            ? steps.length
            : steps.indexOf(selectedStep)

    let icon = (
        <CheckCircleFilled
            style={{
                color: getColor('success'),
                fontSize: 20,
                marginRight: 10,
            }}
        />
    )

    if (stepIndex === selectedStepIndex) {
        icon = (
            <Box
                style={{
                    backgroundColor: getColor('success'),
                }}
                className={styles.stepNumberStyles}
            >
                {stepIndex + 1}
            </Box>
        )
    } else if (stepIndex > selectedStepIndex) {
        icon = <Box className={styles.stepNumberStyles}>{stepIndex + 1}</Box>
    }

    return icon
}

type MoldItemPropsType = {
    moldItem: ImOrderItemType
    moldItemIndex: number
}

const MoldItem = (props: MoldItemPropsType) => {
    const { moldItem, moldItemIndex } = props
    const {
        erpMoldId,
        material,
        processes,
        isOrderStatusPage,
        moldStatus,
        parts,
    } = moldItem

    const { isImReorder } = useContext(OrderContext)

    const partsStatus = isPartsProductionStatusComplete(moldItem?.parts)

    let moldStatusKey

    if (!partsStatus.includes(false)) {
        moldStatusKey = PART_PRODUCTION_COMPLETE.toLowerCase()
    } else {
        moldStatusKey = moldStatus
            ? moldStatus?.toLowerCase()
            : MOLD_DESIGN_IN_PROGRESS?.toLowerCase()
    }

    let steps = moldOrderSteps.get(moldStatusKey)?.steps
    const selectedStep = moldOrderSteps.get(moldStatusKey)?.selectedStep
    const tooltips: string[] = []

    moldOrderSteps.forEach(stepContext => {
        if (stepContext?.tooltip) {
            tooltips.push(stepContext?.tooltip)
        }
    })
    const processNames: (string | null | undefined)[] = []

    Object.keys(parts)?.forEach(partKey => {
        parts[partKey]?.forEach(part => {
            processNames.push(part?.processName?.toLowerCase())
        })
    })

    const isOnlyTSampleOrder: boolean =
        processNames.indexOf(PROCESS_DISPLAY_IM_SAMPLE?.toLowerCase()) >= 0 &&
        new Set(processNames).size === 1

    if (isOnlyTSampleOrder) {
        steps = steps.filter((step: string) => {
            return !partRelatedSteps.includes(step)
        })
    }

    return (
        <Row justify="space-between">
            <Col span={4}>
                <Box
                    style={{
                        display: 'flex',
                        // todo: there is no such css property "alignItem", most likely it supposed to be "alignItems", changing it now might shift layout. Fixing it requires additional testing
                        // alignItem: 'center',
                        justifyContent: 'start',
                    }}
                >
                    <Text fontSize={20} fontWeight={600}>
                        {erpMoldId || `Mold-${moldItemIndex + 1}`}
                        {isImReorder && (
                            <Tag style={{ marginLeft: 5 }}>Existing mold</Tag>
                        )}
                    </Text>
                </Box>
                {!erpMoldId && (
                    <Box>
                        <Text fontSize="11px" fontWeight={200}>
                            Mold information will be available after
                            <br /> processing of the order is complete.
                        </Text>
                    </Box>
                )}
                <Row justify="start">
                    <ImOrderDetailsDisplay
                        material={material}
                        processes={processes}
                        isOrderStatusPage={isOrderStatusPage}
                    />
                </Row>
            </Col>
            <Col span={20}>
                {!isImReorder && (
                    <Steps
                        tooltips={tooltips}
                        steps={steps}
                        selectedStep={selectedStep}
                        getIconForStep={getIcon}
                        style={{
                            padding: '12px',
                        }}
                    />
                )}
            </Col>
        </Row>
    )
}

export default MoldItem
